import { QualityVideo } from "@/components/stage/QualityVideo";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Share2, X, Lock, ShoppingBag, Send, Radio, Gamepad2, Music, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { fmtPrice } from "@/lib/currency";
import { getPost, toggleReact, trackStageView, listComments, addComment, stageShareUrl, typeLabel } from "@/lib/stage";
import { GamePlay } from "@/components/stage/GamePlay";
import { LiveViewer } from "@/components/live/LiveViewer";

export function StagePostViewer({ postId, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");

  useEffect(() => {
    if (!postId) return;
    setPost(null);
    getPost(postId).then((p) => { setPost({ ...p, views: p.views + 1 }); trackStageView(postId); }).catch(() => { toast.error("This post is no longer available"); onClose(); });
    listComments(postId).then((d) => setComments(d.comments || [])).catch(() => {});
  }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!postId) return null;

  const signIn = () => nav(`/register?next=${encodeURIComponent(`/store/${post.seller_id}?post=${post.id}`)}`);
  const react = async () => {
    if (!user) return signIn();
    try {
      const r = await toggleReact(post.id);
      setPost((p) => ({ ...p, reacted: r.reacted, reactions: r.reactions }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not react"); }
  };
  const share = async () => {
    const url = stageShareUrl(post.id);
    const t = `${post.title} — on ${post.seller_name}'s Stage at VISMARKS`;
    if (navigator.share) { try { await navigator.share({ title: t, url }); } catch { /* cancelled */ } return; }
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Link copied — paste it in WhatsApp");
  };
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const c = await addComment(post.id, text.trim());
      setComments((l) => [c, ...l]);
      setPost((p) => ({ ...p, comments: p.comments + 1 }));
      setText("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post your comment"); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="stage-viewer">
      <div className="relative w-full max-w-5xl h-full sm:h-[85vh] bg-[#0A0A0A] sm:rounded-3xl border border-white/10 overflow-hidden grid grid-rows-[auto_1fr] md:grid-rows-1 md:grid-cols-[1.4fr_1fr]" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80" data-testid="stage-viewer-close"><X className="h-5 w-5" /></button>
        <div className="relative bg-black flex items-center justify-center min-h-[40vh]">
          {!post ? <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" /> : <Media post={post} onSignIn={signIn} />}
        </div>
        <div className="flex flex-col min-h-0 border-t md:border-t-0 md:border-l border-white/10">
          {post && (
            <>
              <div className="p-4 border-b border-white/10">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{typeLabel(post.type)}{post.type === "show" && post.live_state === "live" && <span className="ml-2 text-[#FF3B30] font-bold">● LIVE</span>}</div>
                <h2 className="mt-1 text-base md:text-lg font-bold leading-tight" data-testid="stage-viewer-title">{post.title}</h2>
                <Link to={`/store/${post.seller_id}`} className="text-xs text-neutral-400 hover:text-white">by {post.seller_name}</Link>
                {post.caption && <p className="mt-2 text-sm text-neutral-300 whitespace-pre-line">{post.caption}{post.locked && "…"}</p>}
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={react} data-testid="stage-react-btn" className={`rounded-full border-white/20 bg-transparent hover:bg-white/5 ${post.reacted ? "text-[#FF3B30] border-[#FF3B30]/50" : "text-white"}`}><Heart className={`h-4 w-4 mr-1 ${post.reacted ? "fill-current" : ""}`} />{post.reactions}</Button>
                  <Button size="sm" variant="outline" onClick={share} data-testid="stage-share-btn" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5"><Share2 className="h-4 w-4 mr-1" />Share</Button>
                  <span className="ml-auto text-xs text-neutral-500">{post.views} views</span>
                </div>
              </div>
              {post.products?.length > 0 && (
                <div className="p-3 border-b border-white/10 flex gap-2 overflow-x-auto" data-testid="stage-products">
                  {post.products.map((p) => (
                    <Link key={p.id} to={`/product/${p.id}`} className="shrink-0 w-36 rounded-xl border border-white/10 bg-[#111] p-2 hover:border-white/30">
                      {p.image_url ? <img src={p.image_url} alt="" className="h-20 w-full object-cover rounded-lg" /> : <div className="h-20 rounded-lg bg-white/5 flex items-center justify-center"><ShoppingBag className="h-5 w-5 text-neutral-500" /></div>}
                      <div className="mt-1.5 text-xs font-semibold line-clamp-1">{p.title}</div>
                      <div className="text-xs font-mono text-[#34C759]">{fmtPrice(p.price, p.currency)}</div>
                    </Link>
                  ))}
                </div>
              )}
              {post.type === "game" && post.game && <GamePlay post={post} onSignIn={signIn} />}
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3" data-testid="stage-comments">
                {!user && <div className="text-xs text-neutral-500">Sign in free to read and join the conversation.</div>}
                {comments.map((c) => (
                  <div key={c.id} className="text-sm"><span className={`font-semibold ${c.is_seller ? "text-[#FF9F0A]" : "text-white"}`}>{c.name}</span> <span className="text-neutral-300">{c.text}</span></div>
                ))}
                {user && comments.length === 0 && <div className="text-xs text-neutral-500">Be the first to say something.</div>}
              </div>
              {(!post.locked || post.locked_reason === "signin") && <form onSubmit={user ? send : (e) => { e.preventDefault(); signIn(); }} className="p-3 border-t border-white/10 flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={user ? "Say something…" : "Sign in to comment"} readOnly={!user} data-testid="stage-comment-input" className="bg-black/40 border-white/10" />
                <Button type="submit" size="icon" data-testid="stage-comment-send" className="rounded-full bg-white text-black hover:bg-neutral-200"><Send className="h-4 w-4" /></Button>
              </form>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Media({ post, onSignIn }) {
  if (post.locked) {
    return (
      <div className="relative w-full h-full min-h-[40vh]">
        {post.cover_url && <img src={post.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 blur-sm" />}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6" data-testid="stage-locked-overlay">
          <div className="rounded-full bg-white/10 p-4"><Lock className="h-7 w-7" /></div>
          <div className="mt-4 text-lg font-bold">{post.locked_reason === "signin" ? (post.type === "game" ? "Sign in free to play" : post.type === "art" ? "Sign in free to view" : post.type === "audio" ? "Sign in free to listen" : "Sign in free to watch") : post.locked_reason === "ticket" ? `Ticket · $${post.price_usd}` : "Members only"}</div>
          <div className="mt-1 text-sm text-neutral-400 max-w-xs">{post.locked_reason === "signin" ? "Create a free VISMARKS account to enjoy the full Stage, react and shop." : "Tickets and memberships open soon — join the audience to be first."}</div>
          {post.locked_reason === "signin" && <Button onClick={onSignIn} data-testid="stage-signin-btn" className="mt-4 rounded-full bg-[#FF3B30] text-white hover:bg-[#e0352b] font-bold px-6">Sign in free</Button>}
        </div>
      </div>
    );
  }
  if (post.type === "video" && post.media_url) return <QualityVideo post={post} testId="stage-video" />;
  if (post.type === "audio" && post.media_url) {
    return (
      <div className="flex flex-col items-center justify-center p-8 w-full">
        {post.cover_url && <img src={post.cover_url} alt="" className="w-56 h-56 object-cover rounded-2xl shadow-2xl" />}
        <audio src={post.media_url} controls autoPlay className="mt-6 w-full max-w-md" data-testid="stage-audio" />
      </div>
    );
  }
  if (post.type === "show" && post.live_state === "live") return <LiveViewer post={post} />;
  if (post.type === "show" && post.live_state === "ended" && post.media_url) return <QualityVideo post={post} testId="stage-recording" />;
  if (post.type === "show") {
    return (
      <div className="flex flex-col items-center justify-center text-center p-6 w-full h-full relative">
        {post.cover_url && <img src={post.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="relative">
          <Radio className={`h-10 w-10 mx-auto ${post.live_state === "live" ? "text-[#FF3B30] animate-pulse" : "text-neutral-400"}`} />
          <div className="mt-3 text-lg font-bold">{post.live_state === "ended" ? "This show has ended" : "Starts " + (post.scheduled_at ? new Date(post.scheduled_at).toLocaleString() : "soon")}</div>
          {post.live_state === "ended" && <div className="mt-2 text-sm text-neutral-400">{post.has_recording ? "Recording is being prepared." : "No recording was saved for this show."}</div>}
          {post.live_state !== "live" && <div className="mt-2 text-sm text-neutral-400">Join the audience — you'll be notified the moment it goes live.</div>}
        </div>
      </div>
    );
  }
  if (post.cover_url) return <img src={post.cover_url} alt={post.title} className="max-h-full w-full object-contain" data-testid="stage-art" />;
  const I = post.type === "game" ? Gamepad2 : post.type === "audio" ? Music : Play;
  return <div className="w-full h-full min-h-[40vh] flex flex-col items-center justify-center bg-gradient-to-br from-[#2a1b1b] via-[#0A0A0A] to-[#14213a]" data-testid="stage-placeholder"><I className="h-14 w-14 text-white/20" /><div className="mt-3 text-sm text-neutral-500">{post.type === "game" ? "Play on the right →" : post.title}</div></div>;
}
